import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { getChartOptions } from '../chart-utils';
import { AverageAndDeviation, initialAverageAndDeviation } from '../types';
import { findAllMonthLabels, findWeeklyLabels } from '../utils';

enum PerformanceTypes {
  Positive = 'Positive',
  Negative = 'Negative',
}

const PerformanceInidcatorText = styled.span<{
  performanceType: PerformanceTypes;
}>`
  color: ${({ performanceType }) =>
    performanceType === PerformanceTypes.Positive
      ? 'var(--green-50)'
      : 'var(--red-60)'};
`;

export const getDifferenceInWorkingDays = (startDate, endDate) => {
  const lastDay = moment(endDate, 'YYYY-MM-DD');
  const firstDay = moment(startDate, 'YYYY-MM-DD');

  let calcBusinessDays =
    (lastDay.diff(firstDay, 'days') * 5 -
      (firstDay.day() - lastDay.day()) * 2) /
    7;

  // checking if last day is saturday
  if (lastDay.day() === 6) {
    calcBusinessDays -= 1;
  }
  // checking if first day is sunday
  if (firstDay.day() === 0) {
    calcBusinessDays -= 1;
  }

  return calcBusinessDays;
};

export const KeyEventPerformance = ({ plannedDate, activityDate }) => {
  if (!plannedDate) {
    return <>-</>;
  }

  const difference = getDifferenceInWorkingDays(
    plannedDate,
    activityDate ?? moment(),
  );

  if (difference < 0 && !activityDate) {
    return <>-</>;
  }

  return difference === 0 && activityDate ? (
    <PerformanceInidcatorText performanceType={PerformanceTypes.Positive}>
      <FormattedMessage id="key_events.performance_text.on_time" />
    </PerformanceInidcatorText>
  ) : difference > 0 ? (
    <PerformanceInidcatorText performanceType={PerformanceTypes.Negative}>
      <FormattedMessage
        id="key_events.performance_text.delayed"
        values={{ difference }}
      />
    </PerformanceInidcatorText>
  ) : difference < 0 ? (
    <PerformanceInidcatorText performanceType={PerformanceTypes.Positive}>
      <FormattedMessage
        id="key_events.performance_text.early"
        values={{ difference: Math.abs(difference) }}
      />
    </PerformanceInidcatorText>
  ) : (
    <>-</>
  );
};

const processMonthlyData = (monthlyKeyEventData) => {
  let dates: number[] = [];
  const intervalWiseData = (monthlyKeyEventData ?? [])?.reduce(
    (acc, keyEvent) => {
      if (keyEvent.values.length === 0) {
        return acc;
      }
      const toReturn = keyEvent.values?.reduce(
        (acc1, curr) => {
          const currDate = Number(
            `${curr.year}${
              curr.month < 10 ? String(`0${curr.month}`) : curr.month
            }`,
          );

          if (!dates.includes(currDate)) {
            dates = [...dates, currDate];
          }

          return {
            ...acc1,
            average: {
              ...acc1.average,
              [currDate]: {
                y: curr.value,
                growth: curr.growth,
                noOfProjects: curr.projectIds.length,
                name: keyEvent.key,
              },
            },
          };
        },
        { average: {} },
      );

      return {
        ...acc,
        [keyEvent.startKeyEventId]: { ...toReturn, name: keyEvent.key },
      };
    },
    {},
  );

  dates.sort();

  const monthLabels = findAllMonthLabels(dates[0], dates[dates.length - 1]);
  const xAxisDates = Object.keys(monthLabels).sort(function (a, b) {
    return Number(a) - Number(b);
  });
  const xAxisLabels = xAxisDates.map((index) => monthLabels[index]);

  const seriesData = Object.keys(intervalWiseData).reduce(
    (acc, id) => {
      if (intervalWiseData[id]) {
        const { name, average } = intervalWiseData[id];

        const averageData = xAxisDates.map((date) => average[date] ?? {});

        return {
          ...acc,
          average: [...acc.average, { name, data: averageData }],
        };
      }

      return acc;
    },
    { average: [] } as Pick<AverageAndDeviation, 'average'>,
  );

  return { xAxisLabels, seriesData };
};

const processWeeklyData = (weeklyKeyEventData) => {
  let weekDates: number[] = [];
  const weekWiseKeyEventData =
    (weeklyKeyEventData ?? [])?.reduce((acc, keyEvent) => {
      if (keyEvent.values.length === 0) {
        return acc;
      }
      const toReturn = keyEvent.values.reduce((acc1, curr) => {
        const currWeek =
          curr.month === 12 && curr.weekOfMonth === 1
            ? Number(
                `${moment(String(curr.year)).add(1, 'y').format('YYYY')}0101`,
              )
            : Number(
                `${curr.year}${
                  curr.month < 10 ? String(`0${curr.month}`) : curr.month
                }${
                  curr.weekOfMonth < 10
                    ? String(`0${curr.weekOfMonth}`)
                    : curr.weekOfMonth
                }`,
              );

        if (!weekDates.includes(currWeek)) {
          weekDates = [...weekDates, currWeek];
        }
        const weekStartDate = moment(String(currWeek).slice(0, 4))
          .week(Number(String(currWeek).slice(6, 8)))
          .startOf('week');

        return {
          ...acc1,
          average: {
            ...acc1.average,
            [Number(
              `${weekStartDate.format('YY')}${weekStartDate.format(
                'MM',
              )}${weekStartDate.format('DD')}`,
            )]: {
              y: curr.value,
              growth: curr.growth,
              noOfProjects: curr.projectIds.length,
              name: keyEvent.key,
            },
          },
        };
      }, initialAverageAndDeviation);

      return {
        ...acc,
        [keyEvent.startKeyEventId]: { ...toReturn, name: keyEvent.key },
      };
    }, {}) ?? {};

  weekDates.sort();
  const weeklyMonthLabels =
    findWeeklyLabels(weekDates[0], weekDates[weekDates.length - 1]) ?? {};
  const weeklyxAxisDates = Object.keys(weeklyMonthLabels).sort(function (a, b) {
    return Number(a) - Number(b);
  });
  const xAxisLabels = weeklyxAxisDates.map((index) => weeklyMonthLabels[index]);

  const seriesData = Object.keys(weekWiseKeyEventData).reduce(
    (acc, id) => {
      if (weekWiseKeyEventData[id]) {
        const { name, average } = weekWiseKeyEventData[id];
        const averageData = weeklyxAxisDates.map((date) =>
          average ? average[Number(date)] ?? {} : {},
        );

        return {
          ...acc,
          average: [...acc.average, { name, data: averageData }],
        };
      }

      return acc;
    },
    { average: [] } as Pick<AverageAndDeviation, 'average'>,
  );

  return { xAxisLabels, seriesData };
};

const getProcessedData = (keyEventData, zoomLevel) => {
  if (zoomLevel === 'monthly') {
    return processMonthlyData(keyEventData);
  }
  if (zoomLevel === 'weekly') {
    return processWeeklyData(keyEventData);
  }

  return { xAxisLabels: [], seriesData: { average: [] } };
};

export const computeKeyEventData = (KeyEventData, zoomLevel, translate) => {
  const { xAxisLabels, seriesData } = getProcessedData(KeyEventData, zoomLevel);

  const deviationChartOptions = getChartOptions(
    xAxisLabels,
    seriesData.average,
    translate('reports.intervals.deviation_chart.xAxis'),
    translate('reports.intervals.deviation_chart.yAxis'),
  );

  return { deviationChartOptions };
};
